import React, { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { LuPhone, LuMail, LuPencil } from "react-icons/lu";
import EditModal from "../Modal/confirmationModal";
import ConfirmationSuccessModal from "../Modal/confirmationSuccessModal";
import { updateProfile } from "../../redux/user/userSlice";
import { useDispatch } from "react-redux";
import getInitials from "../../utils/getInitailasIMGUtils";

const DARK_NAVY = "#051525";
const DARK_NAVY_LIGHT = "#4a6278";
const BLUE = "#1E3A5F";
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";

interface GeneralDetailsProps {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
  };
}

const cardSx = {
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: CARD_SHADOW,
  border: "1px solid rgba(0,0,0,0.04)",
};

const GeneralDetails: React.FC<GeneralDetailsProps> = ({ user }) => {
  const dispatch = useDispatch<any>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{
    type: "phone" | "email" | "text" | "address";
    value: string;
  }>({ type: "phone", value: "" });

  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

  const handleEditClick = (field: "mobile" | "email") => {
    setModalData({
      type: field === "mobile" ? "phone" : "email",
      value: field === "mobile" ? user.mobile : user.email,
    });
    setIsModalOpen(true);
  };

  const handleModalConfirm = (confirmedValue: string) => {
    handleUpdateProfile(confirmedValue);
    setIsModalOpen(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsConfirmationModalOpen(false);
    setModalData({ type: "phone", value: "" });
  };

  const handleConfirmationConfirm = () => {
    setIsConfirmationModalOpen(false);
    window.location.reload();
  };

  const handleUpdateProfile = async (confirmedValue: string) => {
    let data;
    if (modalData.type === "phone") {
      data = { mobile: confirmedValue };
    } else if (modalData.type === "email") {
      data = { email: confirmedValue };
    }

    try {
      await dispatch(updateProfile(data));
      setIsConfirmationModalOpen(true);
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  const ContactField = ({
    icon: Icon,
    label,
    value,
    onEdit,
  }: {
    icon: React.ElementType;
    label: string;
    value: string;
    onEdit: () => void;
  }) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 220,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          bgcolor: "rgba(30, 58, 95, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={20} color={BLUE} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: DARK_NAVY_LIGHT,
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: DARK_NAVY }}>
            {value || "-"}
          </Typography>
          <IconButton size="small" onClick={onEdit} sx={{ color: BLUE, p: 0.5 }}>
            <LuPencil size={16} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box
        sx={{
          ...cardSx,
          p: { xs: 2.5, md: 3 },
          display: "flex",
          alignItems: "center",
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            bgcolor: BLUE,
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {getInitials(user?.firstName || "A")}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: DARK_NAVY }}>
            {fullName || "-"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: DARK_NAVY_LIGHT, mt: 0.5 }}>
            Admin Account
          </Typography>
        </Box>
      </Box>

      <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3 } }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: DARK_NAVY, mb: 0.5 }}>
          Contact Information
        </Typography>
        <Typography sx={{ fontSize: 13, color: DARK_NAVY_LIGHT, mb: 3 }}>
          Your phone and email details
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 4 },
          }}
        >
          <ContactField
            icon={LuPhone}
            label="Phone Number"
            value={user.mobile}
            onEdit={() => handleEditClick("mobile")}
          />
          <ContactField
            icon={LuMail}
            label="Email Address"
            value={user.email}
            onEdit={() => handleEditClick("email")}
          />
        </Box>
      </Box>

      <EditModal
        open={isModalOpen}
        type={modalData.type}
        value={modalData.value}
        onConfirm={handleModalConfirm}
        onClose={handleModalClose}
      />

      <ConfirmationSuccessModal
        open={isConfirmationModalOpen}
        type={modalData.type}
        value={modalData.value}
        onConfirm={handleConfirmationConfirm}
        onClose={handleModalClose}
      />
    </Box>
  );
};

export default GeneralDetails;
